import { sheetMetrics } from './docStyles';

export const PAGE_PADDING_TOP = 48;
export const PAGE_PADDING_BOTTOM = 44;

export const pageBodyHeight = (landscape = false) =>
  sheetMetrics(landscape).pageHeight - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM;

/**
 * @param {object} params
 * @param {Array<{key: string, height: number, units?: Array<{height: number}>, leadingHeight?: number, headerHeight?: number}>} params.blocks
 * @param {(pageIndex: number) => number} params.capacityFor - Usable height per page.
 * @returns {Array<Array<{key: string, from?: number, to?: number, continued?: boolean}>>} One entry per page.
 */
export const packBlocks = ({ blocks = [], capacityFor }) => {
  const pages = [[]];
  let pageIndex = 0;
  let remaining = capacityFor(0);

  const nextPage = () => {
    pages.push([]);
    pageIndex += 1;
    remaining = capacityFor(pageIndex);
  };

  const isPageEmpty = () => pages[pageIndex].length === 0;

  blocks.forEach((block) => {
    const units = block.units;

    if (!units || !units.length) {
      if (block.height > remaining && !isPageEmpty()) nextPage();

      pages[pageIndex].push({ key: block.key });
      remaining -= block.height;
      return;
    }

    const leadingHeight = block.leadingHeight || 0;
    const headerHeight = block.headerHeight || 0;

    let index = 0;
    let continued = false;

    while (index < units.length) {
      const chromeHeight = (continued ? 0 : leadingHeight) + headerHeight;
      let used = chromeHeight;
      const from = index;

      if (used + units[index].height > remaining && !isPageEmpty()) {
        nextPage();
        continue;
      }

      while (index < units.length && used + units[index].height <= remaining) {
        used += units[index].height;
        index += 1;
      }

      if (index === from) {
        used += units[index].height;
        index += 1;
      }

      pages[pageIndex].push({ key: block.key, from, to: index, continued });
      remaining -= used;
      continued = true;

      if (index < units.length) nextPage();
    }
  });

  return pages.filter((page, index) => index === 0 || page.length > 0);
};

export const tableUnits = (groups = []) => {
  const units = [];

  groups.forEach((group, groupIndex) => {
    if (group?.heading || group?.date) units.push({ type: 'band', groupIndex });
    (group?.rows || []).forEach((row, rowIndex) =>
      units.push({ type: 'row', groupIndex, rowIndex })
    );
  });

  return units;
};

export const sliceTableGroups = ({ groups = [], units = [], from = 0, to = 0 }) => {
  const slice = units.slice(from, to);
  const byGroup = new Map();

  slice.forEach((unit) => {
    if (!byGroup.has(unit.groupIndex)) byGroup.set(unit.groupIndex, []);
    if (unit.type === 'row') byGroup.get(unit.groupIndex).push(unit.rowIndex);
  });

  const bandGroups = new Set(
    slice.filter((unit) => unit.type === 'band').map((unit) => unit.groupIndex)
  );

  return [...byGroup.entries()]
    .map(([groupIndex, rowIndexes]) => {
      const group = groups[groupIndex] || {};
      const hasBand = bandGroups.has(groupIndex);

      return {
        ...group,
        heading: hasBand ? group.heading : undefined,
        date: hasBand ? group.date : undefined,
        note: hasBand ? group.note : undefined,
        key: `${group.key ?? groupIndex}-slice-${from}`,
        rows: rowIndexes.map((rowIndex) => (group.rows || [])[rowIndex]).filter(Boolean)
      };
    })
    .filter((group) => group.rows.length || group.heading || group.date);
};
